import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { Modal } from "app/components/Modal";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  selectCanSubmitItems,
  selectIsAddModalOpen,
  selectItemsForm,
  selectItemsSaving,
} from "../../redux/selector";
import { itemsActions } from "../../redux/slice";
import { ItemsForm } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof ItemsForm;
  label: string;
  multiline?: boolean;
};

const FULL_WIDTH_TOP: FieldConfig[] = [
  { key: "item", label: "ITEM" },
  { key: "detailedDescription", label: "DETAILED_DESCRIPTION", multiline: true },
];

const PAIRED_ROWS: FieldConfig[][] = [
  [
    { key: "height", label: "HEIGHT" },
    { key: "width", label: "WIDTH" },
  ],
  [
    { key: "length", label: "LENGTH" },
    { key: "thickness", label: "THICKNESS" },
  ],
  [
    { key: "weight", label: "WEIGHT" },
    { key: "group", label: "GROUP" },
  ],
];

const FULL_WIDTH_BOTTOM: FieldConfig[] = [{ key: "notes", label: "NOTES" }];

const P = "ITEMS.MODAL";

const AddItemsModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectItemsForm);
  const saving = useSelector(selectItemsSaving);
  const canSubmit = useSelector(selectCanSubmitItems);

  const onClose = () => {
    if (saving) return;
    dispatch(itemsActions.closeAddModal());
  };

  const setField = (name: keyof ItemsForm, value: string) =>
    dispatch(itemsActions.setFormField({ name, value }));

  const renderField = ({ key, label, multiline }: FieldConfig) => (
    <Box key={key} className={styles.field}>
      <Typography className={styles.label}>{t(`${P}.FIELDS.${label}`)}</Typography>
      <TextField
        className={multiline ? styles.textArea : styles.input}
        fullWidth
        multiline={multiline}
        variant="outlined"
        placeholder={t(`${P}.PLACEHOLDERS.ENTER_${label}`)}
        value={form[key]}
        disabled={saving}
        InputProps={{ notched: false }}
        onChange={(event) => setField(key, event.target.value)}
      />
    </Box>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{t(`${P}.ADD_TITLE`)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {FULL_WIDTH_TOP.map(renderField)}
          {PAIRED_ROWS.map((row, index) => (
            <Box key={index} className={styles.grid}>
              {row.map(renderField)}
            </Box>
          ))}
          {FULL_WIDTH_BOTTOM.map(renderField)}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!canSubmit || saving}
            onClick={() => dispatch(itemsActions.addItem())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddItemsModal;
